/**
 * WhyThisCardModal - explainer that opens when a user taps the (i) icon
 * on a SponsoredCard. Establishes that ads are paid, never influence
 * organic match ranking, and that BuildPilot+ removes ads entirely.
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const STATE_FULL = {
  SA: 'South Australia',
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  WA: 'Western Australia',
  TAS: 'Tasmania',
  ACT: 'the Australian Capital Territory',
  NT: 'the Northern Territory',
};

const WhyThisCardModal = ({ state, onClose }) => {
  const region = STATE_FULL[state] || 'your state';

  // Close on Esc key
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // Lock scroll while modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="why-card-title"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(15, 23, 42, 0.45)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      data-testid="why-card-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 440,
          background: '#ffffff',
          borderRadius: 16,
          padding: '24px 24px 20px',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.25)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 id="why-card-title" style={{ fontSize: 20, fontWeight: 600, color: '#0a2540', margin: 0, letterSpacing: '-0.01em' }}>
            Why am I seeing this?
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              border: 0, background: 'transparent', cursor: 'pointer',
              color: '#6b7280', fontSize: 22, lineHeight: 1, padding: '0 4px',
            }}
            data-testid="why-card-modal-close"
          >×</button>
        </div>

        <div style={{ fontSize: 14, lineHeight: 1.55, color: '#374151' }}>
          <p style={{ margin: '0 0 12px 0' }}>
            This is a paid advertisement, not a recommendation from BuildPilot.
          </p>
          <p style={{ margin: '0 0 12px 0' }}>
            We show ads from businesses across {region} that are relevant to what you&apos;re reading.
            Ads <strong>never</strong> influence who appears in our matched builders, suppliers, trades
            or professional services &mdash; those are always ranked by our independent matching logic.
          </p>
          <p style={{ margin: '0 0 20px 0' }}>
            You can hide all ads by upgrading to BuildPilot+.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link
            to="/about/editorial-process"
            onClick={onClose}
            style={{
              flex: '1 1 auto',
              textAlign: 'center',
              padding: '10px 14px',
              background: '#0a2540',
              color: '#ffffff',
              textDecoration: 'none',
              borderRadius: 10,
              fontSize: 13.5,
              fontWeight: 600,
            }}
            data-testid="why-card-learn-matching"
          >
            Learn how matching works
          </Link>
          <Link
            to="/workspace/billing"
            onClick={onClose}
            style={{
              flex: '1 1 auto',
              textAlign: 'center',
              padding: '10px 14px',
              background: '#ffffff',
              color: '#1d1d1f',
              textDecoration: 'none',
              border: '1px solid rgba(15, 23, 42, 0.12)',
              borderRadius: 10,
              fontSize: 13.5,
              fontWeight: 600,
            }}
            data-testid="why-card-see-plus"
          >
            See BuildPilot+
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WhyThisCardModal;
